<template>
  <v-dialog
    :value="value"
    :persistent="isStreaming"
    max-width="800"
    :z-index="zIndex"
    content-class="add-responsibility-dialog__content-class"
    @input="onDialogInput"
  >
    <v-card class="add-responsibility-dialog" width="800">
      <!-- Modal header -->
      <div class="add-responsibility-dialog__header">
        <div class="add-responsibility-dialog__featured-icon" aria-hidden="true">
          <ResponsibilityIcon color="dark" size="normal" />
        </div>
        <div class="add-responsibility-dialog__heading">
          <h2 class="add-responsibility-dialog__title">
            <slot name="title">+ Add Responsibility</slot>
          </h2>
        </div>
        <button
          type="button"
          class="add-responsibility-dialog__close"
          aria-label="Close dialog"
          @click="onClose"
        >
          <v-icon>mdi-close</v-icon>
        </button>
      </div>

      <div v-if="$slots['before-name']" class="add-responsibility-dialog__attach-target">
        <slot name="before-name" />
      </div>

      <!-- Body -->
      <div class="add-responsibility-dialog__body">
        <!-- Responsibility name -->
        <div class="add-responsibility-dialog__field">
          <div class="add-responsibility-dialog__field-header">
            <label class="add-responsibility-dialog__label" for="add-responsibility-dialog__name">
              Responsibility Name*
            </label>
            <span
              v-if="isShowCharCounter && (nameFocused || name.length > 0)"
              class="add-responsibility-dialog__counter"
              :class="{ 'add-responsibility-dialog__counter--error': remainingChars < 0 }"
              aria-live="polite"
            >
              {{ remainingChars }}
            </span>
          </div>
          <input
            id="add-responsibility-dialog__name"
            ref="nameInput"
            v-model="name"
            class="add-responsibility-dialog__input"
            :class="{ 'add-responsibility-dialog__input--error': nameTooLong }"
            type="text"
            placeholder="e.g. Fire Extinguisher Inspection"
            :aria-invalid="nameTooLong || !!nameError ? 'true' : 'false'"
            :aria-describedby="nameError ? 'add-responsibility-dialog__name-error' : undefined"
            @focus="onNameFocus"
            @blur="onNameBlur"
            @input="onNameInput"
          />
          <p
            v-if="nameError"
            id="add-responsibility-dialog__name-error"
            class="add-responsibility-dialog__error"
            role="alert"
          >
            {{ nameError }}
          </p>
        </div>

        <!-- Context + IQ Suggest -->
        <div v-if="showContextField" class="add-responsibility-dialog__field">
          <div class="add-responsibility-dialog__context-label-row">
            <label
              class="add-responsibility-dialog__label"
              for="add-responsibility-dialog__context"
            >
              Context
              <span class="add-responsibility-dialog__optional">(optional)</span>
            </label>
            <div v-if="flagEnabled" class="add-responsibility-dialog__suggest">
              <AskIQButton
                :label="suggestLabel"
                variant="light"
                :height="40"
                :disabled="suggestDisabled"
                @click="handleSuggestClick"
              />
              <template v-if="showReplacePopover">
                <div
                  class="add-responsibility-dialog__replace-backdrop"
                  @click="showReplacePopover = false"
                ></div>
                <div
                  class="add-responsibility-dialog__replace-popover"
                  role="dialog"
                  aria-label="Replace existing context?"
                >
                  <p class="add-responsibility-dialog__replace-title">Replace existing context?</p>
                  <p class="add-responsibility-dialog__replace-body">
                    IQ will overwrite what is currently in Context. You can edit the new text before
                    saving.
                  </p>
                  <div class="add-responsibility-dialog__replace-actions">
                    <button
                      type="button"
                      class="add-responsibility-dialog__replace-btn add-responsibility-dialog__replace-btn--cancel"
                      @click="showReplacePopover = false"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      class="add-responsibility-dialog__replace-btn add-responsibility-dialog__replace-btn--confirm"
                      @click="handleReplace"
                    >
                      Replace
                    </button>
                  </div>
                </div>
              </template>
            </div>
          </div>

          <!-- IQThinkingBanner replaces the textarea while a suggestion runs
               (Connecting / Streaming / Still working / Done / Failed). -->
          <IQThinkingBanner
            v-if="showBanner"
            class="add-responsibility-dialog__thinking-banner"
            :state="bannerState"
            :title="CONNECTING_TITLE"
            :phase="phase"
            :reasoning="reasoning"
            :stalled="stalled"
            :collapsible="bannerCollapsible"
            :hide-retry="true"
            :use-store-reasoning="false"
            :failed-title="FAILED_TITLE"
            :failed-message="FAILED_MESSAGE"
          />
          <div
            v-if="showTextarea"
            class="add-responsibility-dialog__textarea-wrapper"
            :class="{ 'add-responsibility-dialog__textarea-wrapper--error': contextTooLong }"
          >
            <textarea
              id="add-responsibility-dialog__context"
              v-model="context"
              class="add-responsibility-dialog__textarea"
              :maxlength="contextMaxLength"
              :disabled="isStreaming"
              placeholder="What does this responsibility cover? Add scope, boundaries or examples."
              aria-label="Context"
              @focus="onContextFocus"
              @blur="onContextBlur"
            ></textarea>
          </div>

          <div class="add-responsibility-dialog__context-hint-row">
            <p v-if="showTextarea" class="add-responsibility-dialog__hint" aria-live="polite">
              {{ contextHint }}
            </p>
            <span
              v-if="contextFocused"
              class="add-responsibility-dialog__context-counter"
              aria-live="polite"
            >
              {{ context.length }}/{{ contextMaxLength }}
            </span>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="add-responsibility-dialog__footer">
        <button
          type="button"
          class="add-responsibility-dialog__save"
          :disabled="!canSave"
          @click="handleSave"
        >
          <slot name="save-label">{{ saveLabel }}</slot>
        </button>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import AskIQButton from "@components/AskIQButton/AskIQButton.vue";
import IQThinkingBanner from "@components/IQThinkingBanner/IQThinkingBanner.vue";
import ResponsibilityIcon from "@components/icons/items/OrgObject/ResponsibilityIcon.vue";
import { LD_FLAG_IDEA_MDP_617 } from "@components/constants/launchDarklyFlagKeys";
import launchDarklyService from "@/services/launchdarkly";
import { suggestContextResponsibilityStream } from "@/api/suggestContextResponsibilityApi";

const props = defineProps({
  value: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String,
    default: "",
  },
  errorMessage: {
    type: String,
    default: "",
  },
  existingNames: {
    type: Array,
    default: () => [],
  },
  initialValue: {
    type: String,
    default: "",
  },
  initialContext: {
    type: String,
    default: "",
  },
  zIndex: {
    type: [Number, String],
    default: undefined,
  },
  isShowCharCounter: {
    type: Boolean,
    default: true,
  },
  max_char: {
    type: Number,
    default: 120,
  },
  showContext: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  suggestedByIQ: {
    type: Boolean,
    default: false,
  },
  contextMaxLength: {
    type: Number,
    default: 500,
  },
});

const emit = defineEmits(["input", "onSave", "clearError"]);

const NAME_MIN = 3;

// Banner copy (mirrors the AddRoleDialog "Suggest Context" frames).
const CONNECTING_TITLE = "Connecting to IQ";
const FAILED_TITLE = "Couldn’t write context";
const FAILED_MESSAGE =
  "IQ couldn’t suggest context for this responsibility. Try again or write it yourself.";

const name = ref("");
const nameFocused = ref(false);
const context = ref("");
const contextFocused = ref(false);
const showReplacePopover = ref(false);
const flagEnabled = ref(false);
const abortController = ref(null);

// Suggestion state machine. `idle` shows the textarea; `connecting` → `streaming`
// → `stalled` replace it with IQThinkingBanner; `done` collapses the banner above
// the textarea; `failed` shows the banner Failed state.
const suggestionStatus = ref("idle");
const reasoning = ref("");
const phase = ref("");
const stalled = ref(false);

const nameMax = computed(() => (props.max_char && props.max_char > 0 ? props.max_char : 120));
const trimmedName = computed(() => name.value.trim());
const remainingChars = computed(() => nameMax.value - name.value.length);
const nameTooLong = computed(() => trimmedName.value.length > nameMax.value);
const contextTooLong = computed(
  () => (props.contextMaxLength || 0) > 0 && context.value.length > props.contextMaxLength
);
const showContextField = computed(() => flagEnabled.value || props.showContext);

const isStreaming = computed(() =>
  ["connecting", "streaming", "stalled"].includes(suggestionStatus.value)
);
const showBanner = computed(() => suggestionStatus.value !== "idle");
const showTextarea = computed(() =>
  ["idle", "done"].includes(suggestionStatus.value)
);
const bannerState = computed(() => {
  if (suggestionStatus.value === "failed") return "failed";
  if (suggestionStatus.value === "done") return "done";
  return "thinking";
});
const bannerCollapsible = computed(() =>
  ["streaming", "stalled"].includes(suggestionStatus.value)
);

const isDuplicateName = computed(() => {
  const value = trimmedName.value.toLowerCase();
  if (!value) return false;

  const inExistingNames = (props.existingNames || []).some(
    (item) => String(item).trim().toLowerCase() === value
  );
  if (inExistingNames) return true;

  const serverSaysDuplicate =
    !!props.errorMessage && /already exists|already in use|unique/i.test(props.errorMessage);
  return serverSaysDuplicate;
});

const nameError = computed(() => {
  if (nameTooLong.value) {
    return `Responsibility name cannot exceed ${nameMax.value} characters.`;
  }
  if (isDuplicateName.value) {
    return "This name is already in use. Enter a different name.";
  }
  return props.errorMessage || "";
});

const canSave = computed(() => {
  const value = trimmedName.value;
  return (
    value.length >= NAME_MIN &&
    value.length <= nameMax.value &&
    !isDuplicateName.value &&
    !isStreaming.value &&
    !props.loading &&
    !contextTooLong.value
  );
});

const suggestDisabled = computed(() => {
  if (isStreaming.value) return false;
  const value = trimmedName.value;
  return value.length < NAME_MIN || value.length > nameMax.value;
});

const suggestLabel = computed(() => {
  if (isStreaming.value) return "Stop";
  return context.value.trim() ? "Re-suggest" : "Suggest";
});

const saveLabel = computed(() => {
  if (props.loading || isStreaming.value) return "Saving";
  return "Save as Draft";
});

const contextHint = computed(() => {
  if (context.value.trim() || props.suggestedByIQ) {
    return "Suggested by IQ. Review before saving.";
  }
  if (trimmedName.value.length > 0 && trimmedName.value.length < NAME_MIN) {
    return "Enter a name (3+ characters) to enable Suggest.";
  }
  return "Optional. Helps teammates and IQ understand its scope.";
});

function resolveAccountId() {
  try {
    const raw = localStorage.getItem("currentAccountID");
    if (raw == null || raw === "") return null;
    const parsed = JSON.parse(raw);
    return parsed != null ? parsed : raw;
  } catch {
    return localStorage.getItem("currentAccountID");
  }
}

function onDialogInput(nextValue) {
  if (nextValue === true) {
    emit("input", true);
    return;
  }

  if (showReplacePopover.value) {
    showReplacePopover.value = false;
    return;
  }

  if (isStreaming.value) return;

  emit("clearError");
  emit("input", false);
}

function onClose() {
  if (isStreaming.value) return;
  if (showReplacePopover.value) {
    showReplacePopover.value = false;
    return;
  }
  emit("clearError");
  emit("input", false);
}

function onNameInput() {
  if (props.errorMessage) emit("clearError");
}

function onNameFocus() {
  nameFocused.value = true;
}

function onNameBlur() {
  nameFocused.value = false;
}

function onContextFocus() {
  contextFocused.value = true;
}

function onContextBlur() {
  contextFocused.value = false;
}

function handleSuggestClick() {
  if (isStreaming.value) {
    if (abortController.value) abortController.value.abort();
    return;
  }

  if (context.value.trim()) {
    showReplacePopover.value = true;
    return;
  }

  runSuggest();
}

function handleReplace() {
  showReplacePopover.value = false;
  runSuggest();
}

// Advance the banner into Streaming on the first (or resumed) stream event.
// Until the real phase label arrives, the banner defaults to "Writing context …".
function transitionToStreaming() {
  stalled.value = false;
  if (!phase.value) phase.value = "Writing context …";
  suggestionStatus.value = "streaming";
}

async function runSuggest() {
  if (isStreaming.value) return;

  const accountId = resolveAccountId();
  if (accountId == null) {
    suggestionStatus.value = "failed";
    return;
  }

  showReplacePopover.value = false;
  context.value = "";
  reasoning.value = "";
  phase.value = "";
  stalled.value = false;
  suggestionStatus.value = "connecting";

  const controller = new AbortController();
  abortController.value = controller;

  try {
    await suggestContextResponsibilityStream({
      accountId,
      name: trimmedName.value,
      signal: controller.signal,
      onChunk: (text) => {
        context.value = text;
        transitionToStreaming();
      },
      onThinking: (narrative) => {
        reasoning.value = narrative;
        transitionToStreaming();
      },
      onPhase: (label) => {
        phase.value = label;
        transitionToStreaming();
      },
      onStalled: () => {
        stalled.value = true;
        suggestionStatus.value = "stalled";
      },
      onComplete: (text) => {
        context.value = text;
        suggestionStatus.value = "done";
      },
      onError: () => {
        suggestionStatus.value = "failed";
      },
    });
  } catch (error) {
    // Stop (abort) keeps whatever text was already streamed via onChunk and
    // collapses the banner to Done; any other failure shows the Failed state.
    if (error && error.name === "AbortError") {
      suggestionStatus.value = "done";
    } else if (suggestionStatus.value !== "failed") {
      suggestionStatus.value = "failed";
    }
  } finally {
    abortController.value = null;
  }
}

function handleSave() {
  if (!canSave.value) return;
  emit("onSave", { name: trimmedName.value, context: context.value.trim() });
}

watch(
  () => props.value,
  (open) => {
    if (!open) return;

    name.value = props.initialValue || "";
    context.value = props.initialContext || "";
    showReplacePopover.value = false;
    reasoning.value = "";
    phase.value = "";
    stalled.value = false;
    suggestionStatus.value = "idle";

    if (abortController.value) {
      abortController.value.abort();
      abortController.value = null;
    }
  },
  { immediate: true }
);

watch(
  () => props.initialValue,
  (next) => {
    if (props.value && next) name.value = next;
  }
);

watch(
  () => props.initialContext,
  (next) => {
    if (props.value && next) context.value = next;
  }
);

onMounted(async () => {
  try {
    flagEnabled.value = await launchDarklyService.getFlag(LD_FLAG_IDEA_MDP_617, false);
  } catch {
    flagEnabled.value = false;
  }
});
</script>

<style scoped>
.add-responsibility-dialog {
  border-radius: 12px;
  background: #ffffff;
  overflow: hidden;
}

.add-responsibility-dialog__attach-target {
  padding: 24px 24px 0 24px;
}

.add-responsibility-dialog__header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 24px 24px 0 24px;
}

.add-responsibility-dialog__featured-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid #eaecf0;
}

.add-responsibility-dialog__heading {
  flex: 1;
  min-width: 0;
}

.add-responsibility-dialog__title {
  margin: 0;
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 18px;
  line-height: 28px;
  color: #101828;
}

.add-responsibility-dialog__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #98a2b3;
  cursor: pointer;
}

.add-responsibility-dialog__close:hover,
.add-responsibility-dialog__close:focus-visible {
  background: #f9fafb;
  outline: none;
}

.add-responsibility-dialog__body {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
}

.add-responsibility-dialog__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.add-responsibility-dialog__field-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.add-responsibility-dialog__label {
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #344054;
}

.add-responsibility-dialog__optional {
  color: #667085;
}

.add-responsibility-dialog__counter {
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 17px;
  color: #667085;
  white-space: nowrap;
}

.add-responsibility-dialog__counter--error {
  color: #d92d20;
}

.add-responsibility-dialog__input {
  width: 100%;
  height: 44px;
  box-sizing: border-box;
  padding: 10px 14px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #ffffff;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #101828;
}

.add-responsibility-dialog__input::placeholder {
  color: #667085;
}

.add-responsibility-dialog__input:focus {
  outline: none;
  border-color: #7f56d9;
}

.add-responsibility-dialog__input--error {
  border-color: #d92d20;
}

.add-responsibility-dialog__error {
  margin: 0;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 17px;
  color: #d92d20;
}

.add-responsibility-dialog__context-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.add-responsibility-dialog__suggest {
  position: relative;
}

.add-responsibility-dialog__context-label-row :deep(.ask-iq-button) {
  min-width: auto !important;
  justify-content: flex-start !important;
}

.add-responsibility-dialog__textarea-wrapper {
  width: 100%;
  min-height: 150px;
  box-sizing: border-box;
  padding: 12px 14px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #ffffff;
}

.add-responsibility-dialog__textarea-wrapper--error {
  border: 2px solid #d92d20;
}

.add-responsibility-dialog__textarea {
  display: block;
  width: 100%;
  min-height: 124px;
  box-sizing: border-box;
  padding: 0;
  border: none;
  outline: none;
  resize: none;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: #101828;
  background: transparent;
}

.add-responsibility-dialog__textarea::placeholder {
  color: #667085;
}

.add-responsibility-dialog__textarea:disabled {
  color: #98a2b3;
  cursor: not-allowed;
}

.add-responsibility-dialog__context-hint-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.add-responsibility-dialog__hint {
  margin: 0;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 13px;
  line-height: 20px;
  color: #667085;
}

.add-responsibility-dialog__context-counter {
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 17px;
  color: #667085;
  white-space: nowrap;
}

.add-responsibility-dialog__replace-backdrop {
  position: fixed;
  inset: 0;
  z-index: 20;
}

.add-responsibility-dialog__replace-popover {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 21;
  width: 340px;
  box-sizing: border-box;
  padding: 20px;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  background: #ffffff;
  box-shadow:
    0 12px 16px -4px rgba(16, 24, 40, 0.08),
    0 4px 6px -2px rgba(16, 24, 40, 0.03);
}

.add-responsibility-dialog__replace-title {
  margin: 0 0 8px;
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #101828;
}

.add-responsibility-dialog__replace-body {
  margin: 0 0 16px;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #475467;
}

.add-responsibility-dialog__replace-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.add-responsibility-dialog__replace-btn {
  height: 40px;
  padding: 10px 16px;
  border-radius: 8px;
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
}

.add-responsibility-dialog__replace-btn--cancel {
  background: #ffffff;
  border: 1px solid #d0d5dd;
  color: #344054;
}

.add-responsibility-dialog__replace-btn--confirm {
  background: #7f56d9;
  border: 1px solid #7f56d9;
  color: #ffffff;
}

.add-responsibility-dialog__footer {
  padding: 0 24px 24px 24px;
}

.add-responsibility-dialog__save {
  width: 100%;
  height: 44px;
  border: 1px solid #7f56d9;
  border-radius: 8px;
  background: #7f56d9;
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  color: #ffffff;
  cursor: pointer;
}

.add-responsibility-dialog__save:hover:not(:disabled),
.add-responsibility-dialog__save:focus-visible {
  background: #6941c6;
  border-color: #6941c6;
  outline: none;
}

.add-responsibility-dialog__save:disabled {
  background: #e0e0e0;
  border-color: #e0e0e0;
  color: #475467;
  cursor: not-allowed;
}
</style>
